import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { useNavigate } from 'react-router-dom';
import { FormDropdown, Dropdown } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import { toastError } from 'components/Toast';

import {
  getEducatorSettings,
  updateEducatorSettings
} from 'actions/educatorSettings';
import useAsync from 'hooks/useAsync';
import { EducatorSettings, HierarchySettings } from './types';

function PreferencesPage() {
  const navigate = useNavigate();
  const languagesOptions = [
    { key: 'en', value: 'en', text: i18n.ft(messages.english) },
    { key: 'es', value: 'es', text: i18n.ft(messages.spanish) }
  ];
  const { run, isPending } = useAsync();
  const [educatorSettings, setEducatorSettings] = useState<EducatorSettings>({
    language: '',
    age_levels: {}
  });
  const [ageLevels, setAgeLevels] = useState<string[]>([]);
  const [organizationHierarchy, setOrganizationHierarchy] = useState<
    HierarchySettings[]
  >([]);

  useEffect(() => {
    run(getEducatorSettings(), {
      onSuccess: data => {
        let ageLevels = Object.keys(data.age_levels).filter(
          key =>
            data.age_levels[key] &&
            key !== 'Secondary' &&
            key !== 'Upper Elementary'
        );
        const ageLevelOptions = Object.fromEntries(
          Object.entries(data.age_levels).filter(
            ([key]) => key !== 'Secondary' && key !== 'Upper Elementary'
          )
        );

        setEducatorSettings({
          ...data,
          age_levels: ageLevelOptions
        });
        setAgeLevels(ageLevels);
        setOrganizationHierarchy(data.organization_hierarchies);
      },
      onError: () => {
        toastError({ description: i18n.ft(messages.error) });
      }
    });
  }, [run]);

  useEffect(() => {
    // if it's the first time the user is accessing the page and there is only one organization hierarchy, set it as selected
    if (
      organizationHierarchy?.length === 1 &&
      !organizationHierarchy[0]?.selected
    ) {
      updateEducatorSettings({
        age_levels: [...ageLevels],
        language: educatorSettings.language,
        organization_hierarchy_id: organizationHierarchy[0]?.id
      });
    }
  }, [organizationHierarchy, ageLevels, educatorSettings.language]);

  function formatHierarchies(hierarchy: HierarchySettings[]) {
    return hierarchy.map(({ id, name }) => ({
      key: id,
      text: name,
      value: id
    }));
  }

  function getEnglishTranslation(value: string) {
    const translations: Record<string, { text: string; key: string }> = {
      English: { text: 'English', key: 'en' },
      Inglés: { text: 'English', key: 'en' },
      Anglais: { text: 'English', key: 'en' },
      Spanish: { text: 'Spanish', key: 'es' },
      Español: { text: 'Spanish', key: 'es' },
      Espagnol: { text: 'Spanish', key: 'es' },
      French: { text: 'French', key: 'fr' },
      Francés: { text: 'French', key: 'fr' },
      Français: { text: 'French', key: 'fr' }
    };

    return translations[value] || { text: 'English', key: 'en' };
  }

  function handleOnChange(value: string) {
    const translateToEnglish = getEnglishTranslation(value.trim());

    updateEducatorSettings({
      age_levels: [...ageLevels],
      language: translateToEnglish.text,
      organization_hierarchy_id: educatorSettings.organization_hierarchy_id
    });
    setEducatorSettings({
      ...educatorSettings,
      language: translateToEnglish.text
    });
  }

  function handleOnClick(key: string, value: boolean) {
    let newAgeLevels = [...ageLevels];

    if (value) {
      newAgeLevels.push(key);
    } else {
      newAgeLevels = newAgeLevels.filter(age => age !== key);
    }

    setAgeLevels(newAgeLevels);
    updateEducatorSettings({
      language: educatorSettings.language,
      age_levels: [...newAgeLevels],
      organization_hierarchy_id: educatorSettings.organization_hierarchy_id
    });

    setEducatorSettings({
      ...educatorSettings,
      age_levels: { ...educatorSettings.age_levels, [key]: value }
    });
  }

  function handleHierarchyOnChange(value: any) {
    updateEducatorSettings({
      age_levels: [...ageLevels],
      language: educatorSettings.language,
      organization_hierarchy_id: value
    });
    setEducatorSettings({
      ...educatorSettings,
      organization_hierarchies: organizationHierarchy.map(hierarchy => ({
        ...hierarchy,
        selected: hierarchy.id === value
      }))
    });
  }

  function getHierarchyComponent() {
    if (organizationHierarchy.length === 0) {
      return (
        <div className="mt-2 p-3 min-w-[14em]">
          {i18n.ft(messages.noHierarchy)}
        </div>
      );
    }

    if (organizationHierarchy.length === 1) {
      return (
        <input
          type="text"
          disabled
          readOnly
          value={organizationHierarchy[0].name}
          name="organization_hierarchy_id"
          className="mt-2 bg-white rounded-md p-3 min-w-[14em]"
        />
      );
    }

    return (
      <Dropdown
        selection
        className="mt-2"
        selectOnBlur={false}
        value={
          educatorSettings.organization_hierarchies?.find(
            hierarchy => hierarchy.selected
          )?.id || ''
        }
        onChange={(_, { value }) => handleHierarchyOnChange(value)}
        options={formatHierarchies(
          educatorSettings.organization_hierarchies ?? []
        )}
      />
    );
  }

  if (isPending) {
    return null;
  }

  return (
    <div>
      <div className="pt-4 text-[#0A9CCA] font-bold">
        <span
          className="cursor-pointer"
          onClick={() => navigate(RouteHelpers.getPath('learning-resources'))}
        >{`< ${i18n.ft(messages.back)}`}</span>
      </div>
      <div className="text-2xl text-black font-bold w-full mt-8">
        {i18n.ft(messages.learner)}
        <div className="text-[16px] text-black font-normal">
          {i18n.ft(messages.choose)}
        </div>
      </div>

      <div className="text-black mt-8">
        <label className="font-bold text-normal" htmlFor="language_id">
          {i18n.ft(messages.language)}
        </label>
        <FormDropdown
          name="language_id"
          id={'language_id'}
          options={languagesOptions}
          className="rounded-md mt-2"
          selection
          value={
            languagesOptions.find(
              language =>
                language.key ===
                getEnglishTranslation(educatorSettings.language).key
            )?.value || ''
          }
          onChange={e => {
            const target = e.target as HTMLSelectElement;
            handleOnChange(target.innerText);
          }}
        />
      </div>

      <div className="text-black mt-8">
        <label className="font-bold text-normal">{i18n.ft(messages.age)}</label>
        <div className="flex flex-wrap">
          {Object.entries(educatorSettings.age_levels).map(([key, value]) => (
            <div
              key={key}
              className={`mt-2 border border-black rounded-md p-3 mr-3 cursor-pointer ${value ? 'bg-black text-white' : 'bg-{#f3f4f6} text-black'}`}
              onClick={() => handleOnClick(key, !value as boolean)}
            >
              {key}
            </div>
          ))}
        </div>
      </div>

      {organizationHierarchy && (
        <div className="text-black mt-8">
          <div>
            <label
              className="font-bold text-normal"
              htmlFor="organization_hierarchy_id"
            >
              {i18n.ft(messages.hierarchy)}
            </label>
          </div>
          {getHierarchyComponent()}
        </div>
      )}
    </div>
  );
}

export default PreferencesPage;
